import React, { useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

// Helper to convert "yyyy-MM-dd" string to Date object
const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

// Helper to convert Date object to "yyyy-MM-dd" string
const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Reusable date range filter component with quick selection buttons and custom date inputs.
 *
 * @param {Object} value - { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
 * @param {Function} onChange - Called with new { startDate, endDate } when selection changes
 * @param {string} selectedPreset - Currently selected preset key (for highlighting)
 */
function DateRangeFilter({ value, onChange, selectedPreset }) {
  const { t } = useTranslation();

  // Calculate preset date ranges
  const presets = useMemo(() => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    // Helper to get start of week (Monday)
    const getStartOfWeek = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      d.setDate(diff);
      return d;
    };

    return {
      '7d': {
        label: t('Last 7 days'),
        startDate: formatDate(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      },
      '30d': {
        label: t('Last 30 days'),
        startDate: formatDate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      },
      '90d': {
        label: t('Last 90 days'),
        startDate: formatDate(new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      },
      'thisWeek': {
        label: t('This week'),
        startDate: formatDate(getStartOfWeek(today)),
        endDate: formatDate(today),
      },
      'thisMonth': {
        label: t('This month'),
        startDate: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        endDate: formatDate(today),
      },
      'thisYear': {
        label: t('This year'),
        startDate: formatDate(new Date(today.getFullYear(), 0, 1)),
        endDate: formatDate(today),
      },
    };
  }, [t]);

  const handlePresetClick = (presetKey) => {
    const preset = presets[presetKey];
    onChange({
      startDate: preset.startDate,
      endDate: preset.endDate,
      preset: presetKey,
    });
  };

  const handleDateChange = (field, newValue) => {
    onChange({
      ...value,
      [field]: newValue,
      preset: null, // Clear preset when manually changing dates
    });
  };

  return (
    <Box>
      {/* Quick preset buttons */}
      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.75 }, flexWrap: 'wrap', mb: { xs: 2, sm: 1.5 } }}>
        {Object.entries(presets).map(([key, preset]) => (
          <Chip
            key={key}
            label={preset.label}
            onClick={() => handlePresetClick(key)}
            color={selectedPreset === key ? 'primary' : 'default'}
            variant={selectedPreset === key ? 'filled' : 'outlined'}
            size="small"
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Custom date range inputs */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          <DatePicker
            label={t('From')}
            value={dateStringToDate(value.startDate)}
            onChange={(newValue) => handleDateChange('startDate', dateToDateString(newValue))}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: { width: { xs: '100%', sm: 160 } },
              },
            }}
          />
          <DatePicker
            label={t('To')}
            value={dateStringToDate(value.endDate)}
            onChange={(newValue) => handleDateChange('endDate', dateToDateString(newValue))}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: { width: { xs: '100%', sm: 160 } },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
}

export default DateRangeFilter;
